/**
 * Copyright (c) 2024 Spazious, Inc All Rights Reserved
 * Proprietary and confidential
 *
 * Written by Rubeniskov <ruben.lopez@spazious.com>, Thu Feb 15 2024
 */

import { OpaqueHandle } from 'react-reconciler';

import { Instance, InstanceProps, PublicInstance } from '../types.js';

export type Tag = OpaqueHandle;

export type StateNode = {
  _nativeTag: Tag;
  canonical?: {
    nativeTag: Tag;
    publicInstance: PublicInstance;
  };
};

const instanceCache = new Map<Tag, Instance>();
const instanceProps = new Map<Tag, Record<string, any>>();

interface Catalogue {
  [name: string]: React.FC;
}

export const catalogue: Catalogue = {};
// TODO add propper typings
export const extend = (objects: object): void => void Object.assign(catalogue, objects);

export const getComponentType = (name: string): React.FC => {
  return catalogue[name] ?? name;
};

// TODO
export function precacheFiberNode(hostInst: Instance, tag: Tag) {
  instanceCache.set(tag, hostInst);
}

export function uncacheFiberNode(tag: Tag) {
  instanceCache.delete(tag);
  instanceProps.delete(tag);
}

export function getInstanceFromTag(tag: Tag) {
  return instanceCache.get(tag) ?? null;
}

function getTagFromInstance(
  inst: Instance & {
    stateNode: StateNode;
  }
) {
  let nativeInstance = inst.stateNode;
  let tag = nativeInstance._nativeTag;
  if (tag === undefined && nativeInstance.canonical != null) {
    // For compatibility with Fabric
    tag = nativeInstance.canonical.nativeTag;
    nativeInstance = nativeInstance.canonical.publicInstance as any;
  }

  if (!tag) {
    throw new Error('All native instances should have a tag.');
  }

  return nativeInstance;
}

// This function applies a set of changes to the instance
export function applyProps(instance: Instance, mixed: InstanceProps) {
  Object.entries(mixed).forEach(([key, value]) => {
    if (key === 'children') {
      return;
    }
    (instance as any)[key] = value;
  });

  return instance;
}

export {
  getInstanceFromTag as getClosestInstanceFromNode,
  getInstanceFromTag as getInstanceFromNode,
  getTagFromInstance as getNodeFromInstance,
};

export function getFiberCurrentPropsFromNode(stateNode: StateNode) {
  return instanceProps.get(stateNode._nativeTag) || null;
}

export function updateFiberProps(tag: Tag, props: Record<string, any>) {
  instanceProps.set(tag, props);
}
